import {useMutation} from '@tanstack/react-query';
import {MouseEvent, useCallback, useEffect, useState} from 'react';
import {storeMutationAdapter} from '../../../adapter/storeMutationAdapter';
import {t} from '../../../common/getTranslations';
import {openWindow} from '../../../common/openWindow.function';
import {requestContext} from '../../../common/requestContext';
import {CommentStatus} from '../../../common/shared/api/nicolas/vo/enums/CommentStatus.enum';
import {Dialog} from '../../../design-system/organisms/Dialog';
import {
  CommentItem,
  Maybe,
  ResultStatus,
} from '../../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {useLogin} from '../../../user/useLogin.hook';
import {useBlockUserListQuery} from '../../../wrapper';
import {useSetCommentQueryData} from '../hook/useSetCommentQueryData';

interface Props {
  seriesId?: number;
  productId?: number;
  sortType?: Maybe<string>;
  isActiveMoreMenu: boolean;
  isMyComment: boolean;
  deactiveMoreMenu: () => void;
  commentItem: CommentItem;
  parentUid?: number;
  isProductComment?: boolean;
  isOnlyComment?: boolean;
}

export function CommentMoreMenuContainer({
  seriesId,
  productId,
  sortType,
  isActiveMoreMenu,
  deactiveMoreMenu,
  isMyComment,
  commentItem,
  parentUid,
  isProductComment,
  isOnlyComment,
}: Props) {
  const [dialogType, setDialogType] = useState<MoreDialogType | undefined>();
  const {isMobile} = requestContext.requestInfo.display;

  const closeDialog = useCallback(() => {
    deactiveMoreMenu();
    setDialogType(undefined);
  }, [deactiveMoreMenu]);

  useEffect(() => {
    if (isActiveMoreMenu) {
      if (isMyComment) {
        setDialogType('remove_comment');
      } else {
        setDialogType('menu');
      }
    } else {
      setDialogType(undefined);
    }
  }, [closeDialog, isActiveMoreMenu, isMyComment]);

  const {setCommentQueryData} = useSetCommentQueryData({
    seriesId,
    productId,
    commentUid: isOnlyComment ? commentItem?.commentUid : undefined,
    parentUid,
    sortType,
  });
  const {mutateAsync: mutateRemoveComment} = useMutation(
    storeMutationAdapter.removeComment()
  );
  const onClickRemoveComment = useCallback(
    (e: MouseEvent) => {
      LOG_CLICK_EVENT('팝업_삭제_클릭', e, async () => {
        const result = await mutateRemoveComment({
          commentUid: commentItem.commentUid,
        });
        if (result.removeComment.status === ResultStatus.Success) {
          // 삭제후 댓글 목록에서 삭제된 댓글을 찾아서 상태를 변경해줍니다.
          await setCommentQueryData({
            newCommentData: {
              commentUid: commentItem.commentUid,
              commentStatus: CommentStatus.DeletedByMe,
            },
          });
        }
        closeDialog();
      });
    },
    [
      closeDialog,
      commentItem.commentUid,
      mutateRemoveComment,
      setCommentQueryData,
    ]
  );

  const {isLogin, doLogin} = useLogin();
  const onClickDeclareComment = useCallback(
    (e: MouseEvent) => {
      LOG_CLICK_EVENT('신고하기_클릭', e, () => {
        if (!isLogin) {
          doLogin();
          return;
        }
        openWindow({
          url: `https://${window.location.host}/comment/declare?commentUid=${
            commentItem.commentUid
          }${
            isProductComment && commentItem.productId
              ? `&productId=${commentItem.productId}`
              : ''
          }`,
          target: '댓글 신고하기',
          features: `width=540,${isMobile ? 'height=858' : 'height=847'}`,
        });
        closeDialog();
      });
    },
    [
      closeDialog,
      commentItem.commentUid,
      commentItem.productId,
      doLogin,
      isLogin,
      isMobile,
      isProductComment,
    ]
  );

  const onClickClose = useCallback(
    (e: MouseEvent) => {
      LOG_CLICK_EVENT('팝업_취소_클릭', e, () => {
        closeDialog();
      });
    },
    [closeDialog]
  );

  const {data, refetch: refetchBlockUser} = useBlockUserListQuery(undefined, {
    enabled: isLogin,
  });
  const blockUserList = data?.blockUserList?.list || [];
  const isBlockedComment =
    !!commentItem.userUid && blockUserList.includes(commentItem.userUid);

  const onClickBlockUser = useCallback(
    (e: MouseEvent) => {
      LOG_CLICK_EVENT('차단하기_클릭', e, () => {
        if (!isLogin) {
          doLogin();
          return;
        }
        setDialogType('block_user');
      });
    },
    [doLogin, isLogin]
  );

  const {mutateAsync: mutateBlockUser} = useMutation(
    storeMutationAdapter.blockUser()
  );

  const onClickBlockUserConfirm = useCallback(
    (e: MouseEvent) => {
      LOG_CLICK_EVENT('팝업_차단_클릭', e, async () => {
        if (commentItem.userUid) {
          const result = await mutateBlockUser({
            blockUserInput: {
              userUid: commentItem.userUid,
              // 블록되어 있지 않다면 block state가 true (블록한다), 이미 블록되어있었다면 block state가 false (블록을 해제한다) 가 됩니다.
              state: !isBlockedComment,
            },
          });
          if (result.blockUser.status === ResultStatus.Success) {
            await refetchBlockUser();
          }
        }
        closeDialog();
      });
    },
    [
      closeDialog,
      commentItem.userUid,
      isBlockedComment,
      mutateBlockUser,
      refetchBlockUser,
    ]
  );

  if (!isActiveMoreMenu) {
    return null;
  }

  if (dialogType === 'remove_comment') {
    return (
      <Dialog
        isOpen={true}
        onClose={closeDialog}
        isMobile={isMobile}
        {...putTiaraObjectProps({
          customProps: {
            ...(commentItem.productId
              ? {event_episode_id: String(commentItem.productId)}
              : {}),
            event_comment_id: String(commentItem.commentUid),
            popup_type: '삭제확인팝업',
          },
        })}
      >
        <Dialog.Dimmed />
        <Dialog.Title>{t('comment_popup_delete_comment_1')}</Dialog.Title>
        <Dialog.List>
          <Dialog.Label>{t('comment_popup_delete_comment_2')}</Dialog.Label>
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={onClickClose}>
            {t('common_cancel_button')}
          </Dialog.Button>
          <Dialog.Button onClick={onClickRemoveComment}>
            {t('common_delete_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </Dialog>
    );
  }
  if (dialogType === 'menu') {
    return (
      <Dialog
        isOpen={true}
        onClose={closeDialog}
        isMobile={isMobile}
        {...putTiaraObjectProps({
          customProps: {
            ...(commentItem.productId
              ? {event_episode_id: String(commentItem.productId)}
              : {}),
            event_comment_id: String(commentItem.commentUid),
          },
        })}
      >
        <Dialog.Dimmed />
        <Dialog.List className="space-y-0">
          <Dialog.Option onClick={onClickDeclareComment}>
            {t('comment_report')}
          </Dialog.Option>
          <Dialog.Option onClick={onClickBlockUser}>
            {isBlockedComment ? t('comment_release') : t('comment_block')}
          </Dialog.Option>
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={onClickClose}>
            {t('common_cancel_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </Dialog>
    );
  }
  if (dialogType === 'block_user') {
    return (
      <Dialog
        isOpen={true}
        onClose={closeDialog}
        isMobile={isMobile}
        {...putTiaraObjectProps({
          customProps: {
            ...(commentItem.productId
              ? {event_episode_id: String(commentItem.productId)}
              : {}),
            event_comment_id: String(commentItem.commentUid),
            popup_type: '차단확인팝업',
          },
        })}
      >
        <Dialog.Dimmed />
        <Dialog.Title>
          {isBlockedComment
            ? t('comment_popup_release', {
                values: {
                  value: commentItem.userName || ' ',
                },
              })
            : t('comment_popup_block', {
                values: {
                  value: commentItem.userName || ' ',
                },
              })}
        </Dialog.Title>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={onClickClose}>취소</Dialog.Button>
          <Dialog.Button onClick={onClickBlockUserConfirm}>
            {isBlockedComment
              ? t('comment_release_short')
              : t('comment_block_short')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </Dialog>
    );
  }
  return null;
}

type MoreDialogType = 'remove_comment' | 'menu' | 'block_user';
